/**
 * Brand resolution for the dual-published CLI.
 *
 * We ship a single build to two npm packages:
 *   - `@canaryai/cli`  → binary `canary`   (legacy brand)
 *   - `@runcofactor/cli` → binary `cofactor` (new brand)
 *
 * Both packages contain the identical artifact; they differ only in their
 * `name` and `bin` map (rewritten at publish time — see
 * `.github/workflows/publish-canary-cli.yml`). Everything brand-specific is
 * therefore derived at runtime:
 *
 *   - `command` comes from the invoked binary name (`basename(process.argv[1])`),
 *     so output reflects how the user called it (`brandText` rewrites legacy
 *     `canary` display strings to the active brand).
 *   - `packageName` comes from the installed package's own `package.json`, so
 *     self-upgrade and the update check target whatever package was installed.
 *
 * The legacy `canary` brand is the default for every non-`cofactor` invocation
 * (`node dist/index.js`, dev scripts, tests), which keeps existing behaviour
 * byte-for-byte unchanged.
 *
 * @module
 */

import { createRequire } from 'node:module';
import path from 'node:path';
import process from 'node:process';

const require = createRequire(import.meta.url);
const pkg = require('../package.json') as { name: string; version: string };

export type Brand = 'canary' | 'cofactor';

/**
 * Resolve the brand from the invoked binary path (`process.argv[1]`). The
 * global-install shim is named `canary` or `cofactor` (npm/bun/pnpm bin link),
 * so the basename is the brand. Everything else (`node dist/index.js`, dev
 * scripts, tests) defaults to the legacy `canary` brand. Pure for testing.
 */
export function resolveBrandFromArgv(argv1: string | undefined): Brand {
  if (argv1) {
    const base = path.basename(argv1).replace(/\.[^./\\]+$/, '').toLowerCase();
    if (base.includes('cofactor')) return 'cofactor';
  }
  return 'canary';
}

/** The invoked command / binary name, e.g. `canary` or `cofactor`. */
export const command: Brand = resolveBrandFromArgv(process.argv[1]);

/** npm package name of the installed CLI, read from its own package.json. */
export const packageName: string = pkg.name;

/** CLI version, read from package.json. */
export const version: string = pkg.version;

/**
 * Rewrite display strings authored against the legacy `canary` brand to the
 * active brand. Identity for the `canary` brand (so legacy output never
 * changes). Two precise, word-anchored rules:
 *
 *   1. The invocation token `canary ` followed by a lowercase letter (i.e. a
 *      sub-command or `canary v1.2.3`) or `<` (a usage placeholder like
 *      `canary <command> --help`). Anchored on a non-word, non-hyphen
 *      boundary so it never touches `trycanary.ai`, `x-canary-request-id`,
 *      `canary-cli`, or `CANARY_API_TOKEN`.
 *   2. The prose noun `Canary` (capitalized, standalone word) → display name.
 */
const INVOCATION_RE = /(^|[^\w-])canary(?= [a-z<])/g;
const PROSE_RE = /\bCanary\b/g;

/** Pure brand rewrite for a given target brand. Identity for `canary`. */
export function brandTextFor(text: string, brand: Brand): string {
  if (brand === 'canary') return text;
  const display = brand === 'cofactor' ? 'Cofactor' : 'Canary';
  return text
    .replace(INVOCATION_RE, (_m, prefix: string) => `${prefix}${brand}`)
    .replace(PROSE_RE, display);
}

function brandText(text: string): string {
  return brandTextFor(text, command);
}

/**
 * Rewrite a bundled skill template's content to the target brand for the
 * `canary skill install` / `canary skill <name>` output. Builds on
 * {@link brandTextFor} (invocation tokens + the prose noun) and additionally
 * maps the brand-specific identifiers that {@link brandTextFor} deliberately
 * leaves intact for console output — but which a distributed skill file must
 * carry per brand:
 *
 *   - inline binary: `` `canary` `` → `` `cofactor` `` (a backtick-wrapped bare
 *     binary reference, e.g. "the `canary` CLI" — `brandTextFor` only rewrites
 *     `canary <subcommand>` invocations, not the standalone binary name)
 *   - npm package:  `@canaryai/cli`  → `@runcofactor/cli`
 *   - env prefix:   `CANARY_*`       → `COFACTOR_*`
 *   - domains:      `*.trycanary.ai` → `*.runcofactor.com`
 *
 * Identity for the `canary` brand, so legacy skill text installs byte-for-byte
 * unchanged. The skill's frontmatter `name:` is never an invocation/prose/token
 * match (it's `canary-basic`, hyphenated), so the install directory key is
 * preserved. Trigger keywords in prose (e.g. "mentions canary, canary CLI")
 * intentionally keep the legacy term so the skill still activates for users who
 * say "canary". Pure (brand injectable).
 */
export function brandSkillContentFor(content: string, brand: Brand): string {
  const base = brandTextFor(content, brand);
  if (brand !== 'cofactor') return base;
  return base
    .replace(/`canary`/g, '`cofactor`')
    .replace(/@canaryai\/cli/g, '@runcofactor/cli')
    .replace(/\bCANARY_/g, 'COFACTOR_')
    .replace(/trycanary\.ai/g, 'runcofactor.com');
}

/**
 * Read a brand-prefixed environment variable, accepting EITHER brand's prefix.
 *
 * The two published brands historically diverged: the binary only ever read the
 * legacy `CANARY_*` names, but the cofactor docs tell users to set `COFACTOR_*`
 * (e.g. `COFACTOR_API_TOKEN`). This bridges that gap — a value set under either
 * prefix is honoured regardless of which binary (`canary` / `cofactor`) was
 * invoked. When both are set, the active brand's prefix wins.
 *
 * `canaryName` is the legacy `CANARY_`-prefixed name; the `COFACTOR_` variant is
 * derived from it. Empty strings are treated as unset so a blank value can't
 * shadow a real one set under the other prefix. Pure (brand + env injectable).
 */
export function readBrandEnvFor(
  canaryName: string,
  brand: Brand,
  env: NodeJS.ProcessEnv = process.env,
): string | undefined {
  const cofactorName = canaryName.replace(/^CANARY_/, 'COFACTOR_');
  const order = brand === 'cofactor' ? [cofactorName, canaryName] : [canaryName, cofactorName];
  for (const name of order) {
    const value = env[name];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return undefined;
}

/** Read a brand-prefixed env var for the active brand. See {@link readBrandEnvFor}. */
export function readBrandEnv(
  canaryName: string,
  env: NodeJS.ProcessEnv = process.env,
): string | undefined {
  return readBrandEnvFor(canaryName, command, env);
}

/**
 * Workflow spec file convention — brand-aware, backwards compatible.
 *
 * The spec extension and default directory follow the active brand: the
 * `cofactor` binary writes `.cofactor.md` into `cofactor/specs`, the legacy
 * `canary` binary writes `.canary.md` into `canary/specs` (byte-for-byte
 * unchanged). Reading/discovery always accepts BOTH — {@link SPEC_EXTENSIONS}
 * and {@link SPEC_DIRS} list every recognized variant (newest brand first), so
 * an existing `.canary.md` repo keeps working under either binary.
 */
export const SPEC_EXTENSIONS = ['.cofactor.md', '.canary.md'] as const;

/**
 * Default spec-directory roots, newest brand first. Each is used as
 * `<root>/specs`. The ordering is a precedence contract: consumers that merge
 * across roots (e.g. `pull --all`) must treat earlier entries as higher
 * priority, so the active/new brand's copy wins over a legacy one.
 */
export const SPEC_DIRS = ['cofactor', 'canary'] as const;

/** Spec-file extension to WRITE for a given brand. See {@link SPEC_EXTENSIONS}. */
export function specExtensionFor(brand: Brand): string {
  return brand === 'cofactor' ? '.cofactor.md' : '.canary.md';
}

/** Default spec directory (`<root>/specs`) to WRITE into for a given brand. */
export function specDirFor(brand: Brand): string {
  return path.join(brand === 'cofactor' ? 'cofactor' : 'canary', 'specs');
}

/** Spec-file extension to WRITE for the active brand. See {@link specExtensionFor}. */
export const specExtension: string = specExtensionFor(command);

/** Default spec directory to WRITE into for the active brand. See {@link specDirFor}. */
export const specDir: string = specDirFor(command);

/**
 * Every spec directory to READ from, highest precedence first — `<root>/specs`
 * for each {@link SPEC_DIRS} root. This is the single source of the spec-dir
 * convention for discovery; consumers merge across these in order (first-seen
 * wins) so the active brand's copy beats a legacy one. Brand-independent: a
 * `cofactor` run still discovers an existing `canary/specs` repo, and vice
 * versa.
 */
export function readableSpecDirs(): string[] {
  return SPEC_DIRS.map((root) => path.join(root, 'specs'));
}

/** True if `name` ends with any recognized spec extension (either brand). */
export function isSpecFile(name: string): boolean {
  return SPEC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Split a recognized spec extension off `name`, returning `{ base, ext }` for
 * the first matching {@link SPEC_EXTENSIONS} entry, or `null` if none match.
 * Lets callers derive sibling names (e.g. `.stamped`) without assuming a brand.
 */
export function splitSpecExtension(name: string): { base: string; ext: string } | null {
  for (const ext of SPEC_EXTENSIONS) {
    if (name.endsWith(ext)) return { base: name.slice(0, -ext.length), ext };
  }
  return null;
}

/**
 * Print opaque data — anything whose bytes came from a datastore, a server
 * response, or user/customer-controlled input — on a path that branding can
 * never touch.
 *
 * `installConsoleBranding` rewrites the prose noun `Canary` in every string
 * passed to `console.*`. That is correct for authored UI copy and catastrophic
 * for values: a customer record named "Quinn Canary" printed through
 * `console.log` becomes "Quinn Cofactor", and anything parsing that output
 * (e.g. `scripts/copy-flow-to-local.ts`, which reads `psql`'s table form)
 * silently ingests the corrupted value.
 *
 * Gating that risk per-command was the earlier design and was wrong in both
 * directions — it missed data surfaces outside the listed commands (`workflow
 * list` prints workflow names) while disabling branding for authored prose
 * inside them. Provenance is the correct criterion, not the sub-command: if a
 * byte did not originate in this repo's source, render it here.
 *
 * For a mixed line, brand the authored fragment and compose it around the raw
 * value — never hand the composed string to `console.log`.
 */
export function writeRawOutput(value: string): void {
  process.stdout.write(`${value}\n`);
}

/**
 * Whether console output for this invocation should be brand-rewritten.
 *
 * False only for genuinely machine-oriented invocations: the `mcp` JSON-RPC
 * transport and `--json`/`--format` output, where a rewrite could corrupt a
 * structured payload.
 *
 * Deliberately NOT false for any particular sub-command. Data safety is the
 * job of {@link writeRawOutput} at the render site, because a command-level
 * exemption conceals unsafe render sites rather than fixing them. Pure so the
 * policy is testable without touching `console`.
 */
export function shouldBrandConsoleOutput(argv: string[]): boolean {
  if (argv[0] === 'mcp') return false;
  return !argv.includes('--json') && !argv.includes('--format');
}

/**
 * Install brand rewriting over the `console` methods used for human-readable
 * output. No-op for the `canary` brand. The caller gates this on
 * {@link shouldBrandConsoleOutput}, which excludes machine output, the `mcp`
 * JSON-RPC transport, and raw data-query commands — display branding only.
 */
export function installConsoleBranding(): void {
  if (command === 'canary') return;
  const methods = ['log', 'error', 'warn', 'info'] as const;
  for (const method of methods) {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      original(...args.map((arg) => (typeof arg === 'string' ? brandText(arg) : arg)));
    };
  }
}
